import { useMemo } from 'react';
import uniqid from 'uniqid';
import { useNavigate } from 'react-router-dom';

import Icons from 'assets/icons';

import Heading from 'components/partials/heading/heading';
import FlashNotification from 'components/partials/flash-notification/flash-notification';
import AdsBlock from 'components/partials/ads-block/ads-block';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import Loader from 'components/partials/loader/loader';
import ReportSection from 'containers/user/reports/report-section/report-section';
import useProductTypesDictionary from 'containers/user/reports/use-product-types-dictionary';
import useClientProducts from 'containers/user/reports/use-client-products';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';

import { useReports } from 'hooks/api/use-reports';

import { REPORT_TYPES } from 'constants/reports';
import { ADS_BLOCKS } from 'constants/ads-blocks';

import type { ReportTileData } from 'containers/user/reports/report-section/report-tile/report-tile';

const Reports = (): React.JSX.Element => {
  const navigate = useNavigate();
  const reports = useReports();
  const { clientProducts, loading: clientProductsLoading } =
    useClientProducts();
  const { dictionary: productTypesDictionary, loading: dictionaryLoading } =
    useProductTypesDictionary();

  const navigateToReport = (reportId: string | number): void => {
    navigate(`/reports/${reportId}`);
  };

  const handleScheduleCall = (): void => {
    window.location.href = 'mailto:clientsupport@marketviewedu.com';
  };

  const { filteredReports, productTypes, missingAdTypes } = useMemo(() => {
    const reportsData = reports.data as ReportTileData[] | null | undefined;

    if (!reportsData)
      return {
        filteredReports: [] as ReportTileData[],
        productTypes: [] as string[],
        missingAdTypes: [] as string[],
      };

    const filtered = reportsData.filter(
      (report) => report?.powerBiReportType !== REPORT_TYPES.DASHBOARD_1,
    );

    const types = [
      ...new Set(filtered.map((report) => report.productType || 'Other')),
    ];
    const sortedTypes = [...types].sort((a, b) => {
      if (a === 'PROSPECT_VIEW') return -1;
      if (b === 'PROSPECT_VIEW') return 1;

      return a.localeCompare(b);
    });

    const missing: string[] = [];
    const hasRetentionView = clientProducts.some(
      (product) => product.type === REPORT_TYPES.RETENTION_VIEW,
    );
    const hasFinancialView = clientProducts.some(
      (product) => product.type === REPORT_TYPES.FINANCIAL_AID_VIEW,
    );

    const hasProspectView = clientProducts.some(
      (product) => product.type === REPORT_TYPES.PROSPECT_VIEW,
    );

    if (!hasProspectView) {
      missing.push(ADS_BLOCKS.PROSPECT.type);
    }

    if (!hasRetentionView) {
      missing.push(ADS_BLOCKS.RETENTION.type);
    }

    if (!hasFinancialView) {
      missing.push(ADS_BLOCKS.FINANCE.type);
    }

    return {
      filteredReports: filtered,
      productTypes: sortedTypes,
      missingAdTypes: missing,
    };
  }, [reports.data, clientProducts]);

  const isLoading =
    reports.loading || dictionaryLoading || clientProductsLoading;

  const renderAdsBlocks = (): React.JSX.Element | null => {
    if (missingAdTypes.length === 0) return null;

    const isFullWidth = missingAdTypes.length === 1;

    const blocksToRender = Object.values(ADS_BLOCKS).filter((block) =>
      missingAdTypes.includes(block.type),
    );

    return (
      <div className="flex flex-wrap gap-4 mt-10">
        {blocksToRender.map((block) => (
          <AdsBlock
            key={block.id}
            title={block.title}
            description={block.description as unknown as string}
            buttonText={block.buttonText}
            backgroundClass={isFullWidth ? block.bgLarge : block.bgSmall}
            isFullWidth={isFullWidth}
            onButtonClick={handleScheduleCall}
          />
        ))}
      </div>
    );
  };

  const getReportsByType = (productType: string): ReportTileData[] =>
    filteredReports.filter(
      (report) => (report.productType || 'Other') === productType,
    );

  const reportsData = reports.data as ReportTileData[] | null | undefined;

  return (
    <ResponsiveWrapper customClass="mt-8">
      <Heading variant="h2">Reports</Heading>

      <FlashNotification customClass="mt-2 mb-4" />

      {!reportsData?.length && (
        <EmptySpaceContent
          image={<Icons.EmptyImage customClass="w-[210px] h-[210px]" />}
          text="There are no reports to show yet."
          customClass="max-h-[400px] mx-auto mt-5"
        />
      )}

      {isLoading ? (
        <Loader loading={true} customClass="!bg-transparent" />
      ) : (
        <>
          {productTypes.map((productType) => (
            <ReportSection
              key={uniqid()}
              productType={productType}
              reports={getReportsByType(productType)}
              dictionary={productTypesDictionary}
              navigateToReport={navigateToReport}
              isDefaultOpen={true}
            />
          ))}

          {renderAdsBlocks()}
        </>
      )}
    </ResponsiveWrapper>
  );
};

export default Reports;
